import { Card } from '@/components/ui/card';
import { useAutoDetectState } from '@/pages/sale/hooks/use-auto-detect-state';
import { useSaleTabCounters } from '@/pages/sale/hooks/use-sale-tab-counters';
import { SaleTabKey } from '@/pages/sale/lib/sale-tab-config';
import { AutoReply, FbConversation, FBLive, FBPost, QueueCustomer } from '@/types/fb-type';
import { AutoDetectToggle } from './auto-detect';
import { SaleSidebarDesktopTabNav, SaleSidebarMobileTabNav } from './sale-sidebar-tab-nav';
import { SaleSidebarTabPanel } from './sale-sidebar-tab-panel';

export type { SaleTabKey } from '@/pages/sale/lib/sale-tab-config';

type SaleSidebarTabsProps = {
  activeTab: SaleTabKey;
  onTabChange: (tab: SaleTabKey) => void;
  conversations: FbConversation[];
  queueCustomers: QueueCustomer[];
  autoReplies: AutoReply[];
  loadingConversations: boolean;
  onConversationSelect: (conversation: FbConversation) => void;
  posts: FBPost[];
  isLoadingPost: boolean;
  fbLives: FBLive[];
  autoDetectComment?: boolean;
  autoDetectChat?: boolean;
  onPostSelect: (post: FBPost) => void;
  onSelectQueueCustomer: (q: QueueCustomer) => void;
  onSelectLiveVideo: (fbLive: FBLive | null) => void;
  onDropProductOnQueueCustomer?: (
    customer: QueueCustomer,
    productId: string,
    variantId: string,
  ) => void;
  handleUpdateAutoDetectChat?: (flag: boolean) => void;
  handleUpdateAutoDetectComment?: (flag: boolean) => void;
  refetchConversation?: () => Promise<void>;
  loadingFbLives?: boolean;
};

export function SaleSidebarTabs({
  activeTab,
  onTabChange,
  conversations,
  loadingConversations,
  onConversationSelect,
  posts,
  autoReplies,
  isLoadingPost,
  queueCustomers,
  onPostSelect,
  onSelectQueueCustomer,
  onDropProductOnQueueCustomer,
  onSelectLiveVideo,
  autoDetectChat,
  autoDetectComment,
  handleUpdateAutoDetectChat,
  handleUpdateAutoDetectComment,
  fbLives,
  loadingFbLives,
  refetchConversation,
}: SaleSidebarTabsProps) {
  const { detectComment, detectChat, updateDetectComment, updateDetectChat } = useAutoDetectState({
    autoDetectComment,
    autoDetectChat,
    onUpdateComment: handleUpdateAutoDetectComment,
    onUpdateChat: handleUpdateAutoDetectChat,
  });

  const { tabCounters, handleTabChange } = useSaleTabCounters({
    onTabChange,
    refetchConversation,
  });

  return (
    <Card className="h-full flex flex-col overflow-hidden w-full max-w-full">
      <div className="flex shrink-0">
        <AutoDetectToggle
          detectComment={detectComment}
          detectChat={detectChat}
          onChangeComment={updateDetectComment}
          onChangeChat={updateDetectChat}
        />
      </div>

      <SaleSidebarDesktopTabNav
        activeTab={activeTab}
        tabCounters={tabCounters}
        onTabChange={handleTabChange}
      />

      <SaleSidebarMobileTabNav
        activeTab={activeTab}
        tabCounters={tabCounters}
        onTabChange={handleTabChange}
      />

      <SaleSidebarTabPanel
        activeTab={activeTab}
        conversations={conversations}
        loadingConversations={loadingConversations}
        onConversationSelect={onConversationSelect}
        posts={posts}
        isLoadingPost={isLoadingPost}
        onPostSelect={onPostSelect}
        fbLives={fbLives}
        loadingFbLives={loadingFbLives}
        onSelectLiveVideo={onSelectLiveVideo}
        autoReplies={autoReplies}
        queueCustomers={queueCustomers}
        onSelectQueueCustomer={onSelectQueueCustomer}
        onDropProductOnQueueCustomer={onDropProductOnQueueCustomer}
      />
    </Card>
  );
}
